import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

export default function RefundDialog({ reg, onClose, onSaved }) {
  const defaultAmount =
    reg.payment_method === "direct"
      ? Number((reg.price_student || 0) + (reg.price_parent || 0)).toFixed(2)
      : Number(reg.price_parent || 0).toFixed(2);
  const [amount, setAmount] = useState(defaultAmount);
  const [reason, setReason] = useState("");
  const [reverseStripe, setReverseStripe] = useState(reg.payment_method === "direct");
  const [saving, setSaving] = useState(false);

  const canStripeReverse = reg.payment_method === "direct" && !!reg.stripe_session_id;

  const submit = async () => {
    if (!window.confirm(`Refund $${amount} for ${reg.student_name}? This releases their spot.`)) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/registrations/${reg.id}/refund`, {
        amount: Number(amount),
        reason,
        reverse_stripe_charge: canStripeReverse && reverseStripe,
      });
      if (res.data.stripe_error) {
        toast.error(`Refund recorded but Stripe reverse failed: ${res.data.stripe_error}`);
      } else if (res.data.stripe_refund_id) {
        toast.success(`Refund + Stripe reversal complete`);
      } else {
        toast.success("Refund recorded");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund — {reg.student_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="rounded-xl bg-gg-bg border border-gg-border p-3 text-xs space-y-1">
            <div><strong>Invoice:</strong> #{reg.invoice_number}</div>
            <div><strong>Class:</strong> {reg.class_title}</div>
            <div><strong>Payment method:</strong> {reg.payment_method}</div>
            <div><strong>Parent:</strong> {reg.parent_name} · {reg.parent_email}</div>
          </div>
          <div>
            <Label htmlFor="refund-amount">Refund amount (USD)</Label>
            <Input
              id="refund-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="refund-amount-input"
            />
          </div>
          <div>
            <Label htmlFor="refund-reason">Reason (sent to parent)</Label>
            <Textarea
              id="refund-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional — e.g. family emergency, class cancelled..."
              data-testid="refund-reason-input"
            />
          </div>
          {canStripeReverse && (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={reverseStripe}
                onChange={(e) => setReverseStripe(e.target.checked)}
                className="mt-0.5"
                data-testid="refund-reverse-stripe"
              />
              <span>
                <strong>Reverse the Stripe charge</strong> — refunds the card directly via Stripe. Uncheck to record the refund without touching Stripe (you'll process it manually).
              </span>
            </label>
          )}
          {reg.payment_method === "classwallet" && (
            <p className="text-xs text-gg-muted italic">
              ClassWallet refunds are processed through your ClassWallet portal — this dialog only records the refund + emails the parent.
            </p>
          )}
        </div>
        <DialogFooter className="mt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-pill btn-outline"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="btn-pill btn-primary"
            disabled={saving || !amount}
            data-testid="refund-submit-btn"
          >
            {saving ? "Processing…" : "Confirm Refund"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
